import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withQueryParams } from 'src/_shared/services/index.js';
import { useSelector, useDispatch } from 'react-redux';
import { navigationActions } from 'src/actions.js';
import { useApolloClient } from '@apollo/client';
import gql from 'graphql-tag';
import { motion } from 'motion/react';
import {
	DeleteOutlined,
	SaveOutlined,
	ArrowLeftOutlined,
	ArrowRightOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import {
	Button,
	Input,
	Popconfirm,
	Radio,
	message,
	Spin,
	Typography,
	Select,
	Checkbox,
	Alert,
	Card,
	Space,
} from 'antd';
import { Editor } from '@hugerte/hugerte-react';
import dompurify from 'dompurify';

const ALLOWED_IFRAME_SOURCES = [
	'https://www.youtube.com',
	'https://www.youtube-nocookie.com',
	'https://fast.wistia.net',
];

function sanitizeWithIframes(html) {
	const purify = dompurify;

	purify.addHook('uponSanitizeElement', (node, data) => {
		if (data.tagName === 'iframe') {
			const src = node.getAttribute('src');
			if (!src) {
				node.remove();
				return;
			}

			let isAllowed = false;
			try {
				const srcOrigin = new URL(src).origin;
				isAllowed = ALLOWED_IFRAME_SOURCES.includes(srcOrigin);
			} catch {
				isAllowed = false;
			}

			if (!isAllowed) {
				node.remove();
			}
		}
	});

	try {
		return purify.sanitize(html, {
			ADD_TAGS: ['iframe'],
			ADD_ATTR: [
				'src',
				'width',
				'height',
				'frameborder',
				'allowfullscreen',
				'allow',
				'target',
				'rel',
			],
		});
	} finally {
		purify.removeHook('uponSanitizeElement');
	}
}

const { Title } = Typography;

const QUERY_CUSTOM_PAGES = gql`
	query QueryCustomPageByCompanyIdIndex(
		$companyId: ID!
		$first: Int
		$after: String
	) {
		queryCustomPageByCompanyIdIndex(
			companyId: $companyId
			first: $first
			after: $after
		) {
			items {
				id
				active
				companyId
				content
				dateCreated
				hideFromHeader
				pageNumber
				title
				userId
			}
			nextToken
		}
	}
`;

const CREATE_CUSTOM_PAGE = gql`
	mutation CreateCustomPage($input: CreateCustomPageInput!) {
		createCustomPage(input: $input) {
			id
			active
			companyId
			content
			dateCreated
			hideFromHeader
			pageNumber
			title
			userId
		}
	}
`;

const UPDATE_CUSTOM_PAGE = gql`
	mutation UpdateCustomPage($input: UpdateCustomPageInput!) {
		updateCustomPage(input: $input) {
			id
			active
			companyId
			content
			dateCreated
			hideFromHeader
			pageNumber
			title
			userId
		}
	}
`;

const DELETE_CUSTOM_PAGE = gql`
	mutation DeleteCustomPage($input: DeleteCustomPageInput!) {
		deleteCustomPage(input: $input) {
			id
			active
			companyId
			content
			dateCreated
			hideFromHeader
			pageNumber
			title
			userId
		}
	}
`;

const CustomPageBuilderComponent = ({ queryParams }) => {
	let initialIndex = 0;
	if (queryParams?.size > 0 && queryParams.get('pageNumber')) {
		initialIndex = Math.max(0, parseInt(queryParams.get('pageNumber')) - 1);
	}
	const dispatch = useDispatch();
	const updatePathname = (pathname) => {
		dispatch(navigationActions.createUpdatePathname(pathname));
	};
	const currentUser = useSelector((state) => state.user.currentUser);
	const companyId = currentUser?.companyId;
	const client = useApolloClient();

	const [loading, setLoading] = useState(false);
	const [customPagesData, setCustomPagesData] = useState([]);
	const [currentPageIndex, setCurrentPageIndex] = useState(initialIndex);
	const [currentPageContent, setCurrentPageContent] = useState('');
	const [tempTitle, setTempTitle] = useState('');
	const [tempActive, setTempActive] = useState(false);
	const [tempHideFromHeader, setTempHideFromHeader] = useState(false);
	const [editorMode, setEditorMode] = useState('html'); // 'html' or 'rich'
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [previewError, setPreviewError] = useState(null);
	const [debouncedContent, setDebouncedContent] = useState('');
	const [editorWidth, setEditorWidth] = useState(50); // Percentage
	const [containerHeight, setContainerHeight] = useState(600); // Pixels
	const [isDragging, setIsDragging] = useState(false);
	const [isResizingHeight, setIsResizingHeight] = useState(false);
	const [isDividerHovered, setIsDividerHovered] = useState(false);
	const [isHeightHandleHovered, setIsHeightHandleHovered] = useState(false);
	const debounceTimerRef = useRef(null);
	const containerRef = useRef(null);

	const htmlDecode = (input) => {
		let returnValue = input.replaceAll('&gt;', '>');
		returnValue = returnValue.replaceAll('&lt;', '<');
		returnValue = returnValue.replaceAll('&quot;', '"');
		returnValue = returnValue.replaceAll('&apos;', "'");
		returnValue = returnValue.replaceAll('&amp;', '&');
		return returnValue;
	};

	const handleSetCurrentPageContent = useCallback((content) => {
		setCurrentPageContent(content);
		setHasUnsavedChanges(true);

		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		debounceTimerRef.current = setTimeout(() => {
			setDebouncedContent(content);
		}, 1000);
	}, []);

	useEffect(() => {
		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, []);

	const handleHtmlChange = useCallback(
		(e) => {
			const newContent = e.target.value;
			handleSetCurrentPageContent(newContent);
		},
		[handleSetCurrentPageContent]
	);

	const renderRichTextEditor = () => {
		return (
			<Editor
				init={{
					height: '100%',
					menubar: false,
					branding: false,
					resize: false,
					plugins: ['image', 'code', 'media', 'link'],
					toolbar:
						'undo redo | styles | fontfamily | bold italic backcolor | ' +
						'alignleft aligncenter alignright alignjustify | ' +
						'bullist numlist outdent indent | ' +
						'media link image',
					default_link_target: '_blank',
					automatic_uploads: true,
					file_picker_types: 'image',
					content_style:
						"@import url('https://fonts.googleapis.com/css?family=Open+Sans:400,400i,700,700i'); " +
						"body { font-family: 'Open Sans', sans-serif; } " +
						'img { max-width: 100%; } ' +
						'a {color: #018DD3; text-decoration: none !important;}',
					font_family_formats:
						'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica,sans-serif; Impact=impact,chicago; Open Sans=open sans,helvetica neue,helvetica,arial,sans-serif; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva;',
					font_formats:
						'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica neue,helvetica; Impact=impact,chicago; Open Sans=open sans,helvetica neue,helvetica,arial,sans-serif; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva;',
					relative_urls: false,
					convert_urls: false,
					remove_script_host: false,
					setup: function (editor) {
						editor.on('init', function () {
							editor.execCommand('FontName', false, 'Open Sans');
						});
					},
				}}
				value={currentPageContent || ''}
				onEditorChange={handleSetCurrentPageContent}
			/>
		);
	};

	useEffect(() => {
		if (customPagesData[currentPageIndex]) {
			const pageContent = customPagesData[currentPageIndex].content || '';
			const pageTitle = customPagesData[currentPageIndex].title || '';
			const pageActive = customPagesData[currentPageIndex].active || false;
			const pageHideFromHeader = customPagesData[currentPageIndex].hideFromHeader || false;
			setCurrentPageContent(pageContent);
			setTempTitle(pageTitle);
			setTempActive(pageActive);
			setTempHideFromHeader(pageHideFromHeader);
			setDebouncedContent(pageContent);
			setHasUnsavedChanges(false);
			setPreviewError(null);
		}
	}, [currentPageIndex, customPagesData]);

	useEffect(() => {
		updatePathname(window.location.pathname);
		if (companyId) {
			queryCustomPages();
		}
	}, [companyId]);

	const queryCustomPages = async () => {
		if (!loading) {
			setLoading(true);
			try {
				const { data } = await client.query({
					query: QUERY_CUSTOM_PAGES,
					variables: {
						companyId: currentUser.companyId,
						first: 100,
						after: null,
					},
					fetchPolicy: 'network-only',
				});

				const customPages = data?.queryCustomPageByCompanyIdIndex?.items || [];
				const sortedPages = [...customPages].sort((a, b) => a.pageNumber - b.pageNumber);

				setCustomPagesData(sortedPages);
			} catch (error) {
				console.error('Error fetching custom page data:', error);
				message.error('Failed to load custom pages');
			} finally {
				setLoading(false);
			}
		}
	};

	const handleCustomPageMutation = async (type, input) => {
		if (!loading && type) {
			setLoading(true);
			try {
				const mutations = {
					create: { mutation: CREATE_CUSTOM_PAGE, name: 'createCustomPage' },
					update: { mutation: UPDATE_CUSTOM_PAGE, name: 'updateCustomPage' },
					delete: { mutation: DELETE_CUSTOM_PAGE, name: 'deleteCustomPage' },
				};

				const { data } = await client.mutate({
					mutation: mutations[type].mutation,
					variables: {
						input: input,
					},
					fetchPolicy: 'network-only',
					refetchQueries: [
						{
							query: QUERY_CUSTOM_PAGES,
							variables: { companyId: currentUser.companyId },
						},
					],
				});

				return data?.[mutations[type].name] || null;
			} catch (error) {
				console.error('Error updating custom page data:', error);
				message.error(`Failed to ${type} custom page`);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleAddPage = async () => {
		if (hasUnsavedChanges) {
			if (
				!window.confirm(
					'You have unsaved changes. Do you want to discard them and create a new page?'
				)
			) {
				return;
			}
		}

		const newPageNumber = customPagesData.length + 1;
		const input = {
			companyId: currentUser.companyId,
			userId: currentUser.id,
			pageNumber: newPageNumber,
			title: '',
			active: false,
			content: '',
		};

		const result = await handleCustomPageMutation('create', input);
		if (result) {
			await queryCustomPages();
			setCurrentPageIndex(newPageNumber - 1);
			setHasUnsavedChanges(false);
			message.success('New page created');
		}
	};

	const handleSwitchPage = (newIndex) => {
		if (hasUnsavedChanges) {
			if (
				window.confirm('You have unsaved changes. Do you want to discard them?')
			) {
				setCurrentPageIndex(newIndex);
				setHasUnsavedChanges(false);
			}
		} else {
			setCurrentPageIndex(newIndex);
		}
	};

	const handleDeletePage = async (pageId, pageNumber) => {
		const result = await handleCustomPageMutation('delete', { id: pageId });
		if (result) {
			const updatedPages = customPagesData.filter((p) => p.id !== pageId);

			for (let i = pageNumber; i < updatedPages.length + 1; i++) {
				const pageToUpdate = customPagesData.find(
					(p) => p.pageNumber === i + 1
				);
				if (pageToUpdate) {
					await handleCustomPageMutation('update', {
						id: pageToUpdate.id,
						pageNumber: i,
					});
				}
			}

			await queryCustomPages();

			if (currentPageIndex >= updatedPages.length) {
				setCurrentPageIndex(Math.max(0, updatedPages.length - 1));
			}

			message.success('Page deleted');
		}
	};

	const handleMovePage = async (index, direction) => {
		if (direction === 'back' && index === 0) return;
		if (direction === 'forward' && index === customPagesData.length - 1) return;

		if (hasUnsavedChanges) {
			if (
				!window.confirm(
					'You have unsaved changes. Do you want to discard them and move the page?'
				)
			) {
				return;
			}
		}

		const currentPage = customPagesData[index];
		const swappedPage =
			direction === 'back'
				? customPagesData[index - 1]
				: customPagesData[index + 1];
		const currentPageNumber =
			direction === 'back'
				? currentPage.pageNumber - 1
				: currentPage.pageNumber + 1;
		const swappedPageNumber =
			direction === 'back'
				? swappedPage.pageNumber + 1
				: swappedPage.pageNumber - 1;

		await handleCustomPageMutation('update', {
			id: currentPage.id,
			pageNumber: currentPageNumber,
		});

		await handleCustomPageMutation('update', {
			id: swappedPage.id,
			pageNumber: swappedPageNumber,
		});

		await queryCustomPages();
		setCurrentPageIndex(direction === 'back' ? index - 1 : index + 1);
		setHasUnsavedChanges(false);
	};

	const handleSavePageContent = async () => {
		if (previewError) {
			message.error('Cannot save page with syntax errors');
			return;
		}

		const currentPage = customPagesData[currentPageIndex];
		if (!currentPage) return;

		const result = await handleCustomPageMutation('update', {
			id: currentPage.id,
			active: tempActive,
			hideFromHeader: tempHideFromHeader,
			content: currentPageContent,
			title: tempTitle,
		});

		if (result) {
			setHasUnsavedChanges(false);
			message.success('Page saved successfully');
			await queryCustomPages();
		}
	};

	useEffect(() => {
		try {
			if (debouncedContent) {
				sanitizeWithIframes(htmlDecode(debouncedContent));
				setPreviewError(null);
			}
		} catch (error) {
			setPreviewError(error.message);
		}
	}, [debouncedContent]);

	const handleMouseDown = useCallback(() => {
		setIsDragging(true);
	}, []);

	const handleMouseMove = useCallback(
		(e) => {
			if (!isDragging || !containerRef.current) return;

			const container = containerRef.current;
			const containerRect = container.getBoundingClientRect();
			const offsetX = e.clientX - containerRect.left;
			const newWidth = (offsetX / containerRect.width) * 100;

			if (newWidth >= 20 && newWidth <= 80) {
				setEditorWidth(newWidth);
			}
		},
		[isDragging]
	);

	const handleMouseUp = useCallback(() => {
		setIsDragging(false);
		setIsResizingHeight(false);
	}, []);

	const handleHeightMouseDown = useCallback(() => {
		setIsResizingHeight(true);
	}, []);

	const handleHeightMouseMove = useCallback(
		(e) => {
			if (!isResizingHeight || !containerRef.current) return;

			const container = containerRef.current;
			const containerRect = container.getBoundingClientRect();
			const offsetY = e.clientY - containerRect.top;

			if (offsetY >= 300 && offsetY <= 1200) {
				setContainerHeight(offsetY);
			}
		},
		[isResizingHeight]
	);

	useEffect(() => {
		if (isDragging) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
			document.body.style.userSelect = 'none';
			document.body.style.cursor = 'col-resize';
		} else if (isResizingHeight) {
			document.addEventListener('mousemove', handleHeightMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
			document.body.style.userSelect = 'none';
			document.body.style.cursor = 'row-resize';
		} else {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mousemove', handleHeightMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.body.style.userSelect = '';
			document.body.style.cursor = '';
		}

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mousemove', handleHeightMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.body.style.userSelect = '';
			document.body.style.cursor = '';
		};
	}, [
		isDragging,
		isResizingHeight,
		handleMouseMove,
		handleHeightMouseMove,
		handleMouseUp,
	]);

	const renderPreview = () => {
		if (previewError) {
			return (
				<Alert
					message="Preview Error"
					description={previewError}
					type="error"
					showIcon
					icon={<WarningOutlined />}
				/>
			);
		}

		const sanitizedHtml = sanitizeWithIframes(htmlDecode(debouncedContent));
		return (
			<div
				dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
				style={{
					padding: '20px',
					border: '1px solid #d9d9d9',
					borderRadius: '4px',
					height: '100%',
					backgroundColor: '#fff',
					overflow: 'auto',
				}}
			/>
		);
	};

	const currentPage = customPagesData[currentPageIndex];

	return (
		<div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '16px',
				}}
			>
				<Title
					level={2}
					style={{ margin: 0, fontWeight: 700, fontSize: '30px' }}
				>
					Edit Custom Pages
				</Title>

				<div style={{ display: 'flex' }}>
					<Button
						type="link"
						className="add-btn btn-lg create-reward"
						onClick={handleAddPage}
					>
						<span className="icon-circle">
							<b className="icon-plus" />
						</span>
						<span>Add New Page</span>
					</Button>
				</div>
			</div>

			{loading ? (
				<div style={{ textAlign: 'center', padding: '50px' }}>
					<Spin size="large" />
				</div>
			) : (
				<>
					<motion.div
						initial={{ opacity: 0, x: 10 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -10 }}
						transition={{ duration: 0.3, ease: 'easeOut' }}
					>
						{/* Page Management Controls */}
						<Card style={{ marginBottom: '20px', borderRadius: '14px' }}>
							<Space
								direction="vertical"
								size="small"
								style={{ width: '100%' }}
							>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										marginBottom: '8px',
									}}
								>
									{/* Page Selector and Title */}
									<Space wrap align="center">
										<span style={{ fontWeight: 900, fontSize: '16px' }}>
											Change Page:
										</span>
										<Select
											style={{ minWidth: '200px' }}
											value={
												customPagesData?.length > 0 ? currentPageIndex : null
											}
											onChange={handleSwitchPage}
											disabled={customPagesData?.length <= 0}
											placeholder="None Found"
										>
											{customPagesData.map((page, index) => (
												<Select.Option key={page.id} value={index}>
													{page.title || `Custom Page ${page.pageNumber}`}
												</Select.Option>
											))}
										</Select>
									</Space>
									<Space wrap align="center">
										<span style={{ fontSize: '16px', fontWeight: 600 }}>
											<span style={{ fontWeight: 900 }}>Change Order: </span>
											{currentPageIndex + 1}
										</span>
										<Button
											icon={<ArrowLeftOutlined />}
											onClick={() => handleMovePage(currentPageIndex, 'back')}
											disabled={currentPageIndex === 0}
											style={
												currentPageIndex === 0
													? { background: 'none', color: 'rgba(0, 0, 0, 0.25)' }
													: null
											}
										/>

										<Button
											icon={<ArrowRightOutlined />}
											onClick={() =>
												handleMovePage(currentPageIndex, 'forward')
											}
											disabled={
												customPagesData?.length <= 0 ||
												currentPageIndex === customPagesData.length - 1
											}
											style={
												customPagesData?.length <= 0 ||
												currentPageIndex === customPagesData.length - 1
													? { background: 'none', color: 'rgba(0, 0, 0, 0.25)' }
													: null
											}
										/>
									</Space>
								</div>

								{currentPage && (
									<div
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											marginBottom: 0,
										}}
									>
										<Space wrap align="center">
											<span style={{ fontWeight: 900, fontSize: '16px' }}>
												Title:
											</span>
											<Input
												style={{ width: '200px' }}
												value={tempTitle}
												onChange={(e) => {
													setTempTitle(e.target.value);
													setHasUnsavedChanges(true);
												}}
												placeholder={`Custom Page ${currentPageIndex + 1}`}
											/>

											<Checkbox
												checked={tempActive}
												onChange={(e) => {
													setTempActive(e.target.checked);
													setHasUnsavedChanges(true);
												}}
											>
												Active
											</Checkbox>

											<Checkbox
												checked={tempHideFromHeader}
												onChange={(e) => {
													setTempHideFromHeader(e.target.checked);
													setHasUnsavedChanges(true);
												}}
											>
												Hide From Header
											</Checkbox>
										</Space>

										<Space wrap>
											<Button
												type="primary"
												icon={<SaveOutlined />}
												onClick={handleSavePageContent}
												style={
													!hasUnsavedChanges || !!previewError
														? { background: 'none' }
														: null
												}
												disabled={!hasUnsavedChanges || !!previewError}
											/>
											<Popconfirm
												title="Delete this page?"
												description="This action cannot be undone."
												onConfirm={() =>
													handleDeletePage(
														currentPage.id,
														currentPage.pageNumber
													)
												}
												okText="Yes"
												cancelText="No"
												placement="bottomRight"
											>
												<Button
													style={{
														boxShadow: 'none',
													}}
													icon={<DeleteOutlined />}
												/>
											</Popconfirm>
										</Space>
									</div>
								)}
							</Space>
						</Card>

						{/* Editor Section */}
						{currentPage && (
							<Card style={{ borderRadius: '14px' }}>
								<Space
									direction="vertical"
									size="large"
									style={{ width: '100%', height: '100%' }}
								>
									{/* Editor Mode Selector */}
									<Space>
										<Radio.Group
											value={editorMode}
											onChange={(e) => setEditorMode(e.target.value)}
											optionType="button"
											size="small"
											style={{ display: 'flex' }}
										>
											<Radio.Button
												value="html"
												style={{
													borderTopRightRadius: 0,
													borderBottomRightRadius: 0,
													borderRight: 'none',
													boxShadow: 'none',
													padding: '6px',
													margin: 0,
													borderColor:
														'var(--primary-btn-color, var(--theme-primary))',
													backgroundColor:
														editorMode === 'html'
															? 'var(--primary-btn-color, var(--theme-primary))'
															: '#fff',
													color:
														editorMode === 'html'
															? '#fff'
															: 'var(--primary-btn-color, var(--theme-primary))',
												}}
											>
												HTML Editor
											</Radio.Button>
											<Radio.Button
												value="rich"
												style={{
													borderTopLeftRadius: 0,
													borderBottomLeftRadius: 0,
													borderLeft: 'none',
													boxShadow: 'none',
													padding: '6px',
													margin: 0,
													borderColor:
														'var(--primary-btn-color, var(--theme-primary))',
													backgroundColor:
														editorMode === 'rich'
															? 'var(--primary-btn-color, var(--theme-primary))'
															: '#fff',
													color:
														editorMode === 'rich'
															? '#fff'
															: 'var(--primary-btn-color, var(--theme-primary))',
												}}
											>
												Rich Text Editor
											</Radio.Button>
										</Radio.Group>
									</Space>

									{/* Warning for unsaved changes */}
									{hasUnsavedChanges && (
										<Alert
											message="You have unsaved changes"
											type="warning"
											showIcon
											closable
										/>
									)}

									{/* Error alert */}
									{previewError && (
										<Alert
											message="Syntax Error"
											description={previewError}
											type="error"
											showIcon
											icon={<WarningOutlined />}
										/>
									)}

									<div style={{ position: 'relative' }}>
										<div
											ref={containerRef}
											style={{
												display: 'flex',
												gap: '0',
												position: 'relative',
												height: `${containerHeight}px`,
											}}
										>
											{/* Editor */}
											<div
												style={{
													width: `${editorWidth}%`,
													paddingRight: '10px',
													display: 'flex',
													flexDirection: 'column',
													height: '100%',
												}}
											>
												<Title level={5} style={{ marginBottom: '8px' }}>
													{editorMode === 'html'
														? 'HTML Editor'
														: 'Rich Text Editor'}
												</Title>
												{editorMode === 'html' ? (
													<Input.TextArea
														value={currentPageContent}
														onChange={handleHtmlChange}
														style={{
															fontFamily: 'monospace',
															fontSize: '13px',
															flex: 1,
															resize: 'none',
														}}
														placeholder="Enter HTML content here..."
													/>
												) : (
													<div
														style={{
															flex: 1,
															display: 'flex',
															flexDirection: 'column',
														}}
													>
														{renderRichTextEditor()}
													</div>
												)}
											</div>

											{/* Draggable Divider */}
											<div
												onMouseDown={handleMouseDown}
												onMouseEnter={() => setIsDividerHovered(true)}
												onMouseLeave={() => setIsDividerHovered(false)}
												style={{
													width: '2px',
													cursor: 'col-resize',
													backgroundColor: isDragging ? '#1890ff' : '#d9d9d9',
													opacity: isDragging || isDividerHovered ? 1 : 0.5,
													transition: isDragging
														? 'none'
														: 'background-color 0.2s, opacity 0.2s',
													position: 'relative',
													flexShrink: 0,
													userSelect: 'none',
												}}
											>
												<div
													style={{
														position: 'absolute',
														top: '50%',
														left: '50%',
														transform: 'translate(-50%, -50%)',
														width: '20px',
														height: '40px',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														color: '#8c8c8c',
														fontSize: '12px',
													}}
												></div>
											</div>

											{/* Live Preview */}
											<div
												style={{
													width: `${100 - editorWidth}%`,
													paddingLeft: '10px',
													display: 'flex',
													flexDirection: 'column',
													height: '100%',
												}}
											>
												<Title level={5} style={{ marginBottom: '8px' }}>
													Live Preview
												</Title>
												<div style={{ flex: 1, overflow: 'auto' }}>
													{renderPreview()}
												</div>
											</div>
										</div>

										{/* Height Resize Handle */}
										<div
											onMouseDown={handleHeightMouseDown}
											onMouseEnter={() => setIsHeightHandleHovered(true)}
											onMouseLeave={() => setIsHeightHandleHovered(false)}
											style={{
												height: '2px',
												cursor: 'row-resize',
												backgroundColor: isResizingHeight
													? '#1890ff'
													: '#d9d9d9',
												opacity:
													isResizingHeight || isHeightHandleHovered ? 1 : 0.5,
												transition: isResizingHeight
													? 'none'
													: 'background-color 0.2s, opacity 0.2s',
												position: 'relative',
												userSelect: 'none',
												marginTop: '12px',
												marginBottom: '-12px',
												borderRadius: '4px',
											}}
										>
											<div
												style={{
													position: 'absolute',
													top: '50%',
													left: '50%',
													transform: 'translate(-50%, -50%)',
													width: '40px',
													height: '20px',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													color: '#8c8c8c',
													fontSize: '12px',
												}}
											></div>
										</div>
									</div>
								</Space>
							</Card>
						)}

						{/* No pages message */}
						{customPagesData.length === 0 && (
							<Card>
								<div style={{ textAlign: 'center', padding: '40px' }}>
									<Title level={4}>No custom pages yet</Title>
									<p>Click "Add New Page" to create your first custom page.</p>
								</div>
							</Card>
						)}
					</motion.div>
				</>
			)}
		</div>
	);
};

export default withQueryParams(CustomPageBuilderComponent);
